/**
 * @file settings/dev/AiConfigPreview.tsx
 * @brief DEV ONLY: the AI configuration card with seeded settings, to check the
 *        per-step meeting pickers without a superadmin session. Mounted at
 *        `/__preview/ai-config`. Look, don't save: Save calls the real API.
 *
 * @author DES
 * @date 13-10-2026
 */

import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GridBlock } from "@/features/analytics/components/GridBlock";
import { billingKeys } from "@/features/billing/api";
import type { SystemSettingsResponse } from "@/features/billing/schemas";
import { AiConfigurationCard } from "../components/AiConfigurationCard";
import { AiSettingsSimple } from "../components/AiSettingsSimple";
import { settingsKeys, type AiCatalog } from "../api";
import { FALLBACK_AI_CATALOG } from "../schemas";

/**
 * What prod holds after the 13-10-2026 seed. `?routing=length_tier` for the
 * rollback view; `?asr=seeded` adds the speech-to-text chain rows (seeded at
 * the deploy that ships them, 14-10-2026), without it the section is read-only.
 */
function seededSettings(routing: string, asrSeeded: boolean): SystemSettingsResponse {
  const rows: Record<string, string> = {
    LLM_PROVIDER: "openai",
    MEETING_LLM_ROUTING: routing,
    RESCUE_AGENDA_ENABLED: "false",
    OPENAI_MODEL_MEETING_REPORT: "gpt-4.1-mini",
    OPENAI_MODEL_MEETING_REPORT_LONG: "gpt-4.1",
    MEETING_REPORT_LONG_MIN: "90",
    OPENAI_MODEL_VERIFY_JUDGE: "gpt-4.1",
    OPENAI_MODEL_RESCUE: "gpt-4.1-mini",
    OPENAI_MODEL_NOTES_V2: "gpt-4.1-mini",
    OPENAI_MODEL_URDU_SCRIPT: "gpt-4.1-mini",
    OPENAI_MODEL_NOTES: "gpt-4o-mini",
    OPENAI_MODEL_BOARDROOM_SHORT: "gpt-4.1-mini",
    OPENAI_MODEL_BOARDROOM_LONG: "gpt-4.1",
    ...(asrSeeded
      ? {
          ASR_CHAIN_ENABLED: "false",
          ASR_CHAIN_ENGLISH: "soniox,assemblyai,deepgram",
          ASR_CHAIN_URDU: "deepgram,soniox,assemblyai",
          ASR_CHAIN_OTHER: "soniox,deepgram,assemblyai",
          ASR_TIMEOUT_SONIOX_SECS: "1200",
          ASR_TIMEOUT_ASSEMBLYAI_SECS: "1200",
          ASR_TIMEOUT_DEEPGRAM_SECS: "600",
          ASR_MIN_WORDS_PER_MIN: "10",
          ASR_LID_SECONDS: "120",
        }
      : {}),
  };
  return {
    success: true,
    settings: Object.fromEntries(Object.entries(rows).map(([k, value]) => [k, { value }])),
  };
}

/**
 * What prod runs on 23-10-2026 (W13 brief): Azure voice, Soniox listens and
 * translates, chain on with English Soniox first. Stub data, nothing fetched.
 */
function prodToday(): SystemSettingsResponse {
  const base = seededSettings("per_step", true);
  const rows: Record<string, string> = {
    TTS_PROVIDER: "azure",
    ASR_PROVIDER: "soniox",
    MT_PROVIDER: "soniox",
    OPENAI_MODEL_MT: "gpt-4o-mini",
    AZURE_VOICE_UR_MALE: "ur-PK-AsadNeural",
    AZURE_VOICE_UR_FEMALE: "ur-PK-UzmaNeural",
    OPENAI_MODEL_ASK_PLANNER: "gpt-4.1-mini",
    ASR_CHAIN_ENABLED: "true",
    ASR_CHAIN_ENGLISH: "soniox,deepgram,assemblyai",
    // `?b13=1`: the Cost savers rows as the B13 backend seeds them (OFF).
    ...(new URLSearchParams(window.location.search).get("b13") === "1"
      ? { AI_PROMPT_CACHE_ENABLED: "false", AI_PROMPT_SLIM_ENABLED: "false", AI_FLEX_TIER_ENABLED: "false" }
      : {}),
    // `?b16=1`: the B16 / B18 switch rows as 00177 seeds them (OFF).
    ...(new URLSearchParams(window.location.search).get("b16") === "1"
      ? { ASR_URDU_LOANWORDS_LATIN: "false", ASR_SONIOX_VOICE_RESEG: "false", NOTE_REPORT_FOCUSED_INPUT: "false" }
      : {}),
    // `?nano=1`: a saved retired model (W15) on the Ask planner.
    ...(new URLSearchParams(window.location.search).get("nano") === "1" ? { OPENAI_MODEL_ASK_PLANNER: "gpt-4.1-nano" } : {}),
  };
  return { ...base, settings: { ...base.settings, ...Object.fromEntries(Object.entries(rows).map(([k, value]) => [k, { value }])) } };
}

/** The fallback catalog with the keys prod has (Azure, Soniox) and Azure's Urdu voices. */
const PROD_CATALOG: AiCatalog = {
  ...FALLBACK_AI_CATALOG,
  TTS: {
    ...FALLBACK_AI_CATALOG.TTS,
    azure: {
      ...FALLBACK_AI_CATALOG.TTS.azure!,
      configured: true,
      urduVoices: {
        male: [{ value: "ur-PK-AsadNeural", label: "Asad (Pakistan)" }, { value: "ur-IN-SalmanNeural", label: "Salman (India)" }],
        female: [{ value: "ur-PK-UzmaNeural", label: "Uzma (Pakistan)" }, { value: "ur-IN-GulNeural", label: "Gul (India)" }],
      },
    },
  },
  ASR: { ...FALLBACK_AI_CATALOG.ASR, soniox: { ...FALLBACK_AI_CATALOG.ASR.soniox!, configured: true } },
};

/** The simple AI page with prod-like seeded rows (`/__preview/ai-settings`). Look, never Save. */
export function AiSettingsPreviewPage() {
  const [client] = useState(() => {
    const qc = new QueryClient({
      defaultOptions: { queries: { staleTime: Infinity, retry: false, refetchOnMount: false, refetchOnWindowFocus: false } },
    });
    qc.setQueryData(billingKeys.settings(), prodToday());
    qc.setQueryData(settingsKeys.aiCatalog(), PROD_CATALOG);
    return qc;
  });
  return (
    <QueryClientProvider client={client}>
      <GridBlock>
        <AiSettingsSimple advanced={<AiConfigurationCard />} />
      </GridBlock>
    </QueryClientProvider>
  );
}

export function AiConfigPreviewPage() {
  const [client] = useState(() => {
    const routing = new URLSearchParams(window.location.search).get("routing") || "per_step";
    const qc = new QueryClient({
      defaultOptions: { queries: { staleTime: Infinity, retry: false, refetchOnMount: false, refetchOnWindowFocus: false } },
    });
    const asrSeeded = new URLSearchParams(window.location.search).get("asr") === "seeded";
    qc.setQueryData(billingKeys.settings(), seededSettings(routing, asrSeeded));
    qc.setQueryData(settingsKeys.aiCatalog(), FALLBACK_AI_CATALOG);
    return qc;
  });
  return (
    <QueryClientProvider client={client}>
      <GridBlock>
        <AiConfigurationCard />
      </GridBlock>
    </QueryClientProvider>
  );
}
